import React, { useContext, useEffect, useRef, useMemo } from 'react';
import { Box, Typography, IconButton, Avatar, CircularProgress, Paper } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { ChatContext } from '../../contexts/ChatContext';
import { AuthContext } from '../../contexts/AuthContext';
import MessageBubble from './MessageBubble';
import ChatInput from './ChatInput';
import OnlineStatusDot from './OnlineStatusDot';

const STATUS_LABELS = {
  online: 'Online',
  away: 'Abwesend',
  offline: 'Offline'
};

const ChatWindow = () => {
  const {
    activeConversation,
    setActiveConversation,
    messages,
    typingUsers,
    fetchMessages,
    getUserStatus
  } = useContext(ChatContext);
  const { user } = useContext(AuthContext);
  const messagesEndRef = useRef(null);

  const conversationMessages = useMemo(() => messages[activeConversation?.id] || [], [messages, activeConversation?.id]);
  const otherUser = activeConversation?.other_user;
  const isTyping = otherUser && typingUsers.has(otherUser.id);
  const otherUserStatus = otherUser ? getUserStatus(otherUser.id) : 'offline';

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversationMessages, isTyping]);

  // Fetch messages when conversation changes
  useEffect(() => {
    if (activeConversation?.id) {
      fetchMessages(activeConversation.id);
    }
  }, [activeConversation?.id, fetchMessages]);

  if (!activeConversation || !otherUser) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          p: 3
        }}
      >
        <Typography variant="body1" color="text.secondary">
          Wähle eine Conversation oder starte ein neues Gespräch
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}
      >
        <IconButton onClick={() => setActiveConversation(null)} size="small">
          <ArrowBackIcon />
        </IconButton>

        <Box sx={{ position: 'relative' }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            {(otherUser.full_name || otherUser.username).charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ position: 'absolute', bottom: 0, right: 0 }}>
            <OnlineStatusDot
              status={otherUserStatus}
              lastSeenAt={otherUser.last_seen_at}
              size={12}
            />
          </Box>
        </Box>

        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {otherUser.full_name || otherUser.username}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {STATUS_LABELS[otherUserStatus] || 'Offline'}
          </Typography>
        </Box>
      </Paper>

      {/* Messages */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          p: 2,
          backgroundColor: '#fafafa'
        }}
      >
        {conversationMessages.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%'
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Noch keine Nachrichten. Schreibe die erste!
            </Typography>
          </Box>
        ) : (
          <>
            {conversationMessages.map((msg) => (
              <MessageBubble
                key={msg.id}
                message={msg}
                isOwnMessage={msg.sender_id === user?.id}
              />
            ))}

            {isTyping && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Avatar sx={{ width: 24, height: 24, bgcolor: 'grey.400' }}>
                  {otherUser.username.charAt(0).toUpperCase()}
                </Avatar>
                <Paper
                  elevation={1}
                  sx={{
                    px: 2,
                    py: 1,
                    backgroundColor: '#f5f5f5',
                    borderRadius: 2,
                    display: 'flex',
                    gap: 0.5
                  }}
                >
                  <CircularProgress size={8} />
                  <CircularProgress size={8} sx={{ animationDelay: '0.3s' }} />
                  <CircularProgress size={8} sx={{ animationDelay: '0.6s' }} />
                </Paper>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </>
        )}
      </Box>

      {/* Input */}
      <ChatInput
        recipientId={otherUser.id}
        conversationId={activeConversation.id}
      />
    </Box>
  );
};

export default ChatWindow;
